import { useCallback, useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useTier } from "@/context/TierContext";
import { PremiumLock } from "@/components/PremiumLock";
import { EntryLog } from "@/components/EntryLog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  MessagesSquare, Phone, MessageSquare, Mail, Users, ArrowUpRight, ArrowDownLeft,
  Camera, Loader2, Trash2, Pencil, Plus, ShieldCheck, FileText, PenLine, Copy, Check, DoorOpen,
} from "lucide-react";
import { toast } from "sonner";

const CHANNELS = [
  { key: "call", label: "Phone call", Icon: Phone },
  { key: "text", label: "Text message", Icon: MessageSquare },
  { key: "email", label: "Email", Icon: Mail },
  { key: "in_person", label: "In person", Icon: Users },
];
const CH_MAP = Object.fromEntries(CHANNELS.map((c) => [c.key, c]));
const DIRECTIONS = [
  { key: "to_landlord", label: "You → Landlord", Icon: ArrowUpRight },
  { key: "from_landlord", label: "Landlord → You", Icon: ArrowDownLeft },
];
const DIR_MAP = Object.fromEntries(DIRECTIONS.map((d) => [d.key, d]));
const TAGS = [
  { key: "repair", label: "Repair" },
  { key: "inspection", label: "Inspection" },
  { key: "promise", label: "Promise" },
  { key: "notice", label: "Notice" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "");
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const pdfPrefKey = (pid) => `dd_comm_pdf_${pid}`;

export function CommunicationLog({ propertyId }) {
  const { isPremium } = useTier();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [includePdf, setIncludePdf] = useState(() => localStorage.getItem(pdfPrefKey(propertyId)) === "1");
  const [followUp, setFollowUp] = useState(null);
  const [entryLogOpen, setEntryLogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!isPremium) { setLoading(false); return; }
    try { const { data } = await api.get(`/properties/${propertyId}/comm-log`); setEntries(data); }
    catch (e) { console.error("load comm log failed", e); }
    finally { setLoading(false); }
  }, [propertyId, isPremium]);

  useEffect(() => { load(); }, [load]);

  const togglePdf = (v) => {
    setIncludePdf(v);
    localStorage.setItem(pdfPrefKey(propertyId), v ? "1" : "0");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this log entry? This can't be undone.")) return;
    try { await api.delete(`/comm-log/${id}`); setEntries((c) => c.filter((e) => e.entry_id !== id)); toast.success("Entry deleted"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  if (!isPremium) return (
    <PremiumLock title="Communication Timeline" feature="communications" icon={MessagesSquare} testId="comm-locked"
      description="Log landlord communications in one place — they export into your evidence package." />
  );
  if (loading) return null;

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="communication-log">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1 basis-[60%]">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/15 text-brand shrink-0"><MessagesSquare size={20} /></span>
          <div className="min-w-0">
            <h3 className="font-heading font-semibold">Communication log</h3>
            <p className="text-sm text-white/60">A timestamped record of every landlord interaction — calls, texts, emails, promises — so verbal agreements stop disappearing.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end w-full sm:w-auto">
          <Button size="sm" variant="outline" data-testid="entry-log-btn" onClick={() => setEntryLogOpen(true)}
            className="rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/10">
            <DoorOpen size={15} className="mr-1.5" /> Entry log
          </Button>
          <Button size="sm" data-testid="comm-add-btn" onClick={() => { setEditing(null); setFormOpen(true); }}
            className="rounded-xl bg-brand hover:bg-brand-hover text-white">
            <Plus size={15} className="mr-1.5" /> Log interaction
          </Button>
        </div>
      </div>

      {entries.length === 0 ? (
        <p className="mt-4 text-sm text-white/45" data-testid="comm-empty">No interactions logged yet. Add one right after you speak with your landlord — while it's fresh.</p>
      ) : (
        <div className="mt-4 space-y-2.5" data-testid="comm-entries">
          {entries.map((e) => {
            const C = CH_MAP[e.channel] || CH_MAP.call;
            const D = DIR_MAP[e.direction] || DIR_MAP.to_landlord;
            return (
              <div key={e.entry_id} data-testid={`comm-entry-${e.entry_id}`} className="rounded-xl border border-white/10 bg-white/5 p-3.5">
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center h-9 w-9 rounded-lg bg-white/10 text-white/80 shrink-0" title={C.label}><C.Icon size={16} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-white/80">{C.label}</span>
                      <span className="inline-flex items-center gap-1 text-[11px] text-white/50"><D.Icon size={12} /> {D.label}</span>
                      {(e.tags || []).map((t) => (
                        <span key={t} data-testid={`comm-tag-${e.entry_id}-${t}`} className="inline-flex items-center rounded-full bg-brand/20 text-brand text-[10px] font-semibold px-2 py-0.5 capitalize">{t}</span>
                      ))}
                    </div>
                    <p className="mt-1 text-sm text-white/90 whitespace-pre-wrap break-words">{e.summary}</p>
                    <p className="mt-1.5 text-[11px] text-white/40 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span>Occurred {fmtDate(e.occurred_at)}</span>
                      <span className="text-white/25">·</span>
                      <span className="inline-flex items-center gap-1"><ShieldCheck size={11} className="text-emerald-300" /> Logged {fmtDateTime(e.logged_at)}</span>
                    </p>
                    {e.screenshots?.length > 0 && (
                      <div className="mt-2 flex gap-2 overflow-x-auto">
                        {e.screenshots.map((p) => (
                          <a key={p.photo_id} href={fileUrl(p.storage_path)} target="_blank" rel="noopener noreferrer"
                            data-testid={`comm-shot-${p.photo_id}`}
                            className="h-14 w-14 rounded-lg overflow-hidden border border-white/10 shrink-0">
                            <img src={fileUrl(p.storage_path)} alt="screenshot" className="h-full w-full object-cover" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" variant="ghost" data-testid={`comm-followup-${e.entry_id}`}
                      onClick={() => setFollowUp(e)}
                      className="rounded-lg h-8 w-8 p-0 text-brand hover:bg-brand/15" title="Confirm in writing (email draft)"><PenLine size={13} /></Button>
                    {e.editable && (
                      <Button size="sm" variant="ghost" data-testid={`comm-edit-${e.entry_id}`} onClick={() => { setEditing(e); setFormOpen(true); }}
                        className="rounded-lg h-8 w-8 p-0 text-white/70 hover:bg-white/10" title="Edit (24h window)"><Pencil size={13} /></Button>
                    )}
                    <Button size="sm" variant="ghost" data-testid={`comm-delete-${e.entry_id}`} onClick={() => remove(e.entry_id)}
                      className="rounded-lg h-8 w-8 p-0 text-[#FCA5A5] hover:bg-[#7F1D1D]/40" title="Delete"><Trash2 size={13} /></Button>
                  </div>
                </div>
                {!e.editable && (
                  <p className="mt-2 text-[10px] uppercase tracking-wide text-white/30" data-testid={`comm-frozen-${e.entry_id}`}>Frozen for integrity</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-3">
        <div className="flex items-start gap-2 min-w-0">
          <FileText size={15} className="text-white/60 mt-0.5 shrink-0" />
          <span className="text-xs text-white/70">Include this log as an appendix in the Evidence Package PDF</span>
        </div>
        <Switch data-testid="comm-pdf-toggle" checked={includePdf} onCheckedChange={togglePdf} />
      </div>

      {formOpen && (
        <EntryDialog propertyId={propertyId} entry={editing}
          onClose={() => setFormOpen(false)}
          onSaved={(saved, isEdit) => {
            setFormOpen(false);
            setEntries((c) => isEdit ? c.map((x) => x.entry_id === saved.entry_id ? saved : x) : [saved, ...c]);
          }} />
      )}
      {followUp && <FollowUpDialog entry={followUp} onClose={() => setFollowUp(null)} />}
      {entryLogOpen && <EntryLog propertyId={propertyId} onClose={() => setEntryLogOpen(false)} />}
    </div>
  );
}

function EntryDialog({ propertyId, entry, onClose, onSaved }) {
  const isEdit = !!entry;
  const [channel, setChannel] = useState(entry?.channel || "call");
  const [direction, setDirection] = useState(entry?.direction || "from_landlord");
  const [summary, setSummary] = useState(entry?.summary || "");
  const [occurredAt, setOccurredAt] = useState(entry?.occurred_at?.slice(0, 10) || todayISO());
  const [tags, setTags] = useState(entry?.tags || []);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const toggleTag = (k) => setTags((cur) => (cur.includes(k) ? cur.filter((t) => t !== k) : [...cur, k]));

  const save = async () => {
    const s = summary.trim();
    if (!s) { toast.error("Add a short summary."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/comm-log/${entry.entry_id}`, {
          channel, direction, summary: s, occurred_at: occurredAt, tags,
        });
        toast.success("Entry updated");
        onSaved(data, true);
      } else {
        const form = new FormData();
        form.append("channel", channel);
        form.append("direction", direction);
        form.append("summary", s);
        form.append("occurred_at", occurredAt);
        form.append("tags", tags.join(","));
        files.forEach((f) => form.append("files", f));
        const { data } = await api.post(`/properties/${propertyId}/comm-log`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Interaction logged");
        onSaved(data, false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="comm-dialog" className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit interaction" : "Log an interaction"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Entries can be edited within 24 hours of logging, then they freeze." : "Capture what was said while it's fresh. The logged time is recorded automatically."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Channel</Label>
            <select data-testid="comm-channel-select" value={channel} onChange={(e) => setChannel(e.target.value)}
              className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
              {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Direction</Label>
            <select data-testid="comm-direction-select" value={direction} onChange={(e) => setDirection(e.target.value)}
              className="mt-1 h-10 w-full rounded-xl border border-input bg-background px-3 text-sm">
              {DIRECTIONS.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">When did it happen?</Label>
            <Input data-testid="comm-date-input" type="date" value={occurredAt} max={todayISO()}
              onChange={(e) => setOccurredAt(e.target.value)} className="mt-1 h-10 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs">Summary</Label>
            <Textarea data-testid="comm-summary-input" value={summary} maxLength={500}
              onChange={(e) => setSummary(e.target.value)} rows={4} className="mt-1 rounded-xl"
              placeholder="e.g. Landlord agreed by text to repair the kitchen leak within a week." />
            <p className="mt-1 text-[11px] text-muted-foreground text-right">{summary.length}/500</p>
          </div>
          <div>
            <Label className="text-xs">Tags (optional)</Label>
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="comm-tags">
              {TAGS.map((t) => (
                <button key={t.key} type="button" data-testid={`comm-tag-toggle-${t.key}`} onClick={() => toggleTag(t.key)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${tags.includes(t.key) ? "bg-brand border-brand text-white" : "border-input text-muted-foreground hover:border-brand"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {!isEdit && (
            <div>
              <Label className="text-xs">Screenshots (optional)</Label>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                data-testid="comm-file-input" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="mt-1 w-full rounded-xl" data-testid="comm-pick-btn">
                <Camera size={16} className="mr-2" /> {files.length > 0 ? `${files.length} image${files.length === 1 ? "" : "s"} selected` : "Attach screenshots"}
              </Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="comm-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !summary.trim()} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="comm-save">
            {saving ? <><Loader2 size={16} className="animate-spin mr-1.5" /> Saving…</> : (isEdit ? "Save changes" : "Log it")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FollowUpDialog({ entry, onClose }) {
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [copied, setCopied] = useState(false);

  const generate = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/comm-log/${entry.entry_id}/follow-up`);
      setDraft(data);
      setSubject(data.subject || "");
      setBody(data.body || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't draft the follow-up.");
      onClose();
    } finally { setLoading(false); }
  }, [entry.entry_id, onClose]);

  useEffect(() => { generate(); }, [generate]);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
      setCopied(true);
      toast.success("Email copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy — select and copy manually."); }
  };

  const openMail = () => {
    const to = draft?.landlord_email || "";
    const href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="comm-followup-dialog" className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirm this in writing</DialogTitle>
          <DialogDescription>A courteous email that puts your landlord interaction on record. Review, tweak, then copy or send.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 py-10 justify-center text-muted-foreground" data-testid="comm-followup-loading">
            <Loader2 size={18} className="animate-spin" /> Drafting your follow-up…
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Subject</Label>
              <Input data-testid="comm-followup-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 h-10 rounded-xl" />
            </div>
            <div>
              <Label className="text-xs">Email body</Label>
              <Textarea data-testid="comm-followup-body" value={body} onChange={(e) => setBody(e.target.value)} rows={12} className="mt-1 rounded-xl" />
            </div>
            <p className="text-[11px] text-muted-foreground">This is a factual confirmation — it does not make legal claims. Review before sending.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="comm-followup-close">Close</Button>
          <Button variant="outline" onClick={copyEmail} disabled={loading} className="rounded-xl" data-testid="comm-followup-copy">
            {copied ? <><Check size={16} className="mr-1.5" /> Copied</> : <><Copy size={16} className="mr-1.5" /> Copy email</>}
          </Button>
          <Button onClick={openMail} disabled={loading} className="rounded-xl bg-brand hover:bg-brand-hover text-white" data-testid="comm-followup-send">
            <Mail size={16} className="mr-1.5" /> Open in email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

